import React from "react";
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Modal,
  Paper,
} from "@mui/material";
import { useAppSelector, useIsMobile } from "../../app/hooks";
import { selectLabels, selectLanguage } from "../../slices/language";
import { selectUser } from "../../slices/user";
import dayjs from "dayjs";
import Logo from "../Logo";
import IconifyIcon from "../IconifyIcon";
import "./printable-pass.css";
import { getPropertyLogoUrl } from "../../utils/getPropertyLogoUrl";

export interface PrintablePassSnapshot {
  visitorName: string;
  receiveName: string;
  department: string;
  registeredAt: string;
  accessType: "entrance" | "exit";
}

interface PrintablePassProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  data: PrintablePassSnapshot | null;
  onFinished: () => void;
}

type Phase = "menu" | "camera" | "preview";

const LABEL_W = 540;
const LABEL_H = 360;

const PrintablePass: React.FC<PrintablePassProps> = ({
  open,
  data,
  onFinished,
}) => {
  const labels = useAppSelector(selectLabels);
  const language = useAppSelector(selectLanguage);
  const user = useAppSelector(selectUser);
  const property = user?.property;
  const propertyImage = getPropertyLogoUrl(property);
  const lang = language.startsWith("es") ? "es" : "en";
  const isMobile = useIsMobile();

  const [phase, setPhase] = React.useState<Phase>("menu");
  const [photoDataUrl, setPhotoDataUrl] = React.useState<string | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const labelPrintRef = React.useRef<HTMLDivElement>(null);
  const previewWrapRef = React.useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = React.useState(1);
  const [cameras, setCameras] = React.useState<MediaDeviceInfo[]>([]);
  const [activeCameraDeviceId, setActiveCameraDeviceId] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    if (phase !== "preview" || !open) return;
    const el = previewWrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth - 16;
      if (w > 0) setPreviewScale(Math.min(1, w / LABEL_W));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [phase, open, photoDataUrl]);

  const stopStream = React.useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  /** Cada apertura (o cierre) del flujo debe volver al primer paso (menú). */
  React.useEffect(() => {
    if (!open) {
      setPhase("menu");
      setPhotoDataUrl(null);
      setCameras([]);
      setActiveCameraDeviceId(null);
      stopStream();
      return;
    }
    if (!data) return;
    setPhase("menu");
    setPhotoDataUrl(null);
    setCameras([]);
    setActiveCameraDeviceId(null);
    stopStream();
  }, [open, data, stopStream]);

  const startCameraStream = React.useCallback(
    async (deviceId: string | null) => {
      stopStream();
      const videoConstraints: MediaTrackConstraints = deviceId
        ? {
            deviceId: { exact: deviceId },
            width: { ideal: 720 },
            height: { ideal: 960 },
          }
        : {
            facingMode: { ideal: "user" },
            width: { ideal: 720 },
            height: { ideal: 960 },
            aspectRatio: { ideal: 3 / 4 },
          };

      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false,
      });

      streamRef.current = stream;
      const settings = stream.getVideoTracks()[0]?.getSettings();
      setActiveCameraDeviceId(settings?.deviceId ?? deviceId ?? null);

      const devices = await navigator.mediaDevices.enumerateDevices();
      setCameras(devices.filter((d) => d.kind === "videoinput"));

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    },
    [stopStream],
  );

  const handleSelectCamera = React.useCallback(
    (deviceId: string) => {
      if (deviceId === activeCameraDeviceId) return;
      void startCameraStream(deviceId).catch((err) => {
        console.error("Error switching camera", err);
        stopStream();
      });
    },
    [activeCameraDeviceId, startCameraStream, stopStream],
  );

  React.useEffect(() => {
    if (phase !== "camera" || !open) {
      if (phase !== "camera") {
        stopStream();
        setCameras([]);
        setActiveCameraDeviceId(null);
      }
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        await startCameraStream(null);
        if (cancelled) stopStream();
      } catch (err) {
        if (!cancelled) {
          console.error("Error starting camera", err);
          stopStream();
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [phase, open, startCameraStream, stopStream]);

  const formattedDateTime = React.useMemo(() => {
    if (!data?.registeredAt) return "";
    const d = dayjs(data.registeredAt);
    return lang === "es"
      ? d.format("DD/MM/YY HH:mm")
      : d.format("MM/DD/YY hh:mm A");
  }, [data?.registeredAt, lang]);

  const datetimeLabel =
    data?.accessType === "exit"
      ? labels.printable_pass_salida
      : labels.printable_pass_entra;

  const handleCapture = React.useCallback(() => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement("canvas");
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const targetRatio = 3 / 4;
    const frameRatio = vw / vh;
    let sx = 0;
    let sy = 0;
    let sw = vw;
    let sh = vh;
    if (frameRatio > targetRatio) {
      sw = Math.round(vh * targetRatio);
      sx = Math.round((vw - sw) / 2);
    } else {
      sh = Math.round(vw / targetRatio);
      sy = Math.round((vh - sh) / 2);
    }
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
    setPhotoDataUrl(canvas.toDataURL("image/jpeg", 0.92));
    stopStream();
    setPhase("preview");
  }, [stopStream]);

  const handlePrint = React.useCallback(async () => {
    const node = labelPrintRef.current;
    if (!node) return;

    const printStyles = `
      @page {
        size: ${LABEL_W}px ${LABEL_H}px;
        margin: 0;
      }
  
      html, body {
        margin: 0;
        padding: 0;
        width: ${LABEL_W}px;
        height: ${LABEL_H}px;
        overflow: hidden;
        background: #fff;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
  
      * {
        box-sizing: border-box;
      }
  
      .printable-pass-label-root {
        width: ${LABEL_W}px !important;
        height: ${LABEL_H}px !important;
        box-sizing: border-box;
        background: #fff;
        display: flex;
        flex-direction: row;
        font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
        color: #111;
        border: none !important;
        box-shadow: none !important;
        overflow: hidden;
      }
  
      .printable-pass-brand-logo {
        display: flex;
        align-items: flex-start;
        max-width: 100%;
        overflow: hidden;
      }
  
      .printable-pass-brand-logo img {
        max-width: 200px !important;
        max-height: 72px !important;
        width: auto !important;
        height: auto !important;
        object-fit: contain !important;
        display: block !important;
      }
  
      .printable-pass-brand-logo-sm img {
        max-height: 18px !important;
        width: auto !important;
        display: block !important;
      }
  
      img {
        max-width: 100%;
      }
    `;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");

    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    const win = iframe.contentWindow;

    if (!doc || !win) {
      document.body.removeChild(iframe);
      return;
    }

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Pass</title>
          <style>${printStyles}</style>
        </head>
        <body>
          ${node.outerHTML}
        </body>
      </html>
    `);
    doc.close();

    const waitForImages = async () => {
      const images = Array.from(doc.images);
      await Promise.all(
        images.map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
          });
        }),
      );
    };

    try {
      await waitForImages();

      if ("fonts" in doc) {
        try {
          await (doc as Document & { fonts: FontFaceSet }).fonts.ready;
        } catch {}
      }

      setTimeout(() => {
        const cleanup = () => {
          setTimeout(() => {
            iframe.remove();
          }, 200);
        };

        if ("onafterprint" in win) {
          win.onafterprint = cleanup;
        }

        win.focus();
        win.print();

        if (!("onafterprint" in win)) {
          cleanup();
        }
      }, 150);
    } catch {
      iframe.remove();
    }
  }, [onFinished]);

  const handleContinueMenu = () => {
    onFinished();
  };

  const handleOpenCamera = () => {
    setPhotoDataUrl(null);
    setPhase("camera");
  };

  const handleBackToMenu = () => {
    stopStream();
    setPhase("menu");
    setPhotoDataUrl(null);
  };

  const handleBackToCamera = () => {
    setPhotoDataUrl(null);
    setPhase("camera");
  };

  if (!open || !data) return null;

  const labelBody = (
    <div
      ref={labelPrintRef}
      className="printable-pass-label-root"
      style={{
        width: LABEL_W,
        height: LABEL_H,
        boxSizing: "border-box",
        background: "#fff",
        display: "flex",
        flexDirection: "row",
        fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
        // color: "#111",
        // border: "1px solid #ddd",
        padding: 6,
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: "16px 12px 12px 16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="printable-pass-brand-logo"
          style={{
            display: "flex",
            alignItems: "flex-start",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {propertyImage ? (
            <img
              src={propertyImage}
              alt={property?.name ? `${property.name} logo` : "Property logo"}
              style={{
                maxWidth: 200,
                maxHeight: 72,
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : (
            <Logo type="passTrackLogo" alt="PassTrack" />
          )}
        </div>
        <div style={{ fontSize: 16, fontWeight: 500, marginTop: 16 }}>
          Visitante:
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 22,
            fontWeight: 700,
            lineHeight: 1.15,
            wordBreak: "break-word",
          }}
        >
          {data.visitorName}
        </div>
        <div
          style={{
            marginTop: "auto",
            fontSize: 13,
            lineHeight: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div style={{ fontSize: 15, display: "flex" }}>
            <span style={{ fontWeight: 600, minWidth: 72 }}>
              {datetimeLabel}:
            </span>
            <span>{formattedDateTime}</span>
          </div>
          <div style={{ fontSize: 15, display: "flex" }}>
            <span style={{ fontWeight: 600, minWidth: 72 }}>
              {labels.printable_pass_recibe}:
            </span>
            <span style={{ wordBreak: "break-word" }}>{data.receiveName}</span>
          </div>
          <div style={{ fontSize: 15, display: "flex" }}>
            <span style={{ fontWeight: 600, minWidth: 72 }}>
              {labels.printable_pass_depto}:
            </span>
            <span style={{ wordBreak: "break-word" }}>{data.department}</span>
          </div>
        </div>
      </div>
      <div
        style={{
          width: 230,
          boxSizing: "border-box",
          padding: "12px 12px 12px 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            borderRadius: 8,
            overflow: "hidden",
            background: "#e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {photoDataUrl ? (
            <img
              src={photoDataUrl}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : null}
        </div>
        <div
          style={{
            marginTop: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 6,
            fontSize: 10,
            color: "#555",
          }}
        >
          <span>{labels.printable_pass_powered_by}</span>
          <div className="printable-pass-brand-logo-sm">
            <Logo type="passTrackLogo" alt="" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onFinished}>
      <Box
        style={{
          width: "100%",
          height: "100%",
          padding: 12,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper className="printable-pass-dialog-paper" elevation={0}
          sx={{ width: "100%", maxWidth: 620, maxHeight: "100%", overflowY: "auto", overflowX: "hidden", minWidth: 0 }}>

          {phase === "menu" && (
            <Box style={{ padding: 24 }}>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                alignItems="stretch"
              >
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ px: 6 }}
                  onClick={handleOpenCamera}
                >
                  {labels.printable_pass_print_pass}
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ px: 6 }}
                  onClick={handleContinueMenu}
                >
                  {labels.printable_pass_continue}
                </Button>
              </Box>
            </Box>
          )}

          {phase === "camera" && (
            <>
              <DialogTitle>{labels.printable_pass_camera_title}</DialogTitle>
              <DialogContent>
                <p className="body-text-sm subtitle-color mb-4">
                  {labels.printable_pass_camera_hint}
                </p>
                <div className="printable-pass-camera-preview">
                  <div className="printable-pass-video-wrap">
                    <video ref={videoRef} playsInline muted autoPlay />
                  </div>
                </div>
                {!isMobile && cameras.length > 1 && (
                  <Box mt={2}>
                    <p className="body-text-xs font-w-500 mb-2">
                      {labels.printable_pass_select_camera}
                    </p>
                    <List
                      dense
                      disablePadding
                      className="printable-pass-camera-list"
                    >
                      {cameras.map((cam, index) => {
                        const isActive = cam.deviceId === activeCameraDeviceId;
                        const label =
                          cam.label?.trim() ||
                          `${labels.printable_pass_camera_unnamed} ${index + 1}`;
                        return (
                          <ListItemButton
                            key={cam.deviceId}
                            selected={isActive}
                            onClick={() => handleSelectCamera(cam.deviceId)}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {isActive ? (
                                <IconifyIcon
                                  icon="material-symbols:check-circle"
                                  style={{ fontSize: 22, color: "#10418a" }}
                                />
                              ) : (
                                <IconifyIcon
                                  icon="material-symbols:radio-button-unchecked"
                                  style={{ fontSize: 22, color: "#9e9e9e" }}
                                />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={label}
                              primaryTypographyProps={{
                                variant: "body2",
                                style: { wordBreak: "break-word" },
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Box>
                )}
                <Box
                  display="flex"
                  gap={2}
                  justifyContent="center"
                  flexWrap="wrap"
                  mt={3}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCapture}
                  >
                    {labels.printable_pass_capture}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleBackToMenu}
                  >
                    {labels.printable_pass_cancel_camera}
                  </Button>
                </Box>
              </DialogContent>
            </>
          )}

          {phase === "preview" && photoDataUrl && (
            <>
              <DialogTitle>{labels.printable_pass_preview_title}</DialogTitle>
              <DialogContent>
                <div
                  ref={previewWrapRef}
                  className="printable-pass-preview-scroll"
                  style={{ minHeight: LABEL_H * previewScale + 16 }}
                >
                  <div style={{ width: LABEL_W * previewScale, height: LABEL_H * previewScale, flexShrink: 0 }}>
                  <div
                    className="printable-pass-label-scale"
                    style={{
                      transform: `scale(${previewScale})`,
                      width: LABEL_W,
                      height: LABEL_H,
                    }}
                  >
                    {labelBody}
                  </div>
                  </div>
                </div>
                <Box
                  display="flex"
                  gap={2}
                  justifyContent="center"
                  flexWrap="wrap"
                  mt={2}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handlePrint}
                  >
                    {labels.printable_pass_print}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleBackToCamera}
                  >
                    {labels.printable_pass_retake}
                  </Button>
                  <Button variant="text" size="small" onClick={onFinished}>
                    {labels.printable_pass_skip_print}
                  </Button>
                </Box>
              </DialogContent>
            </>
          )}
        </Paper>
      </Box>
    </Modal>
  );
};

export default PrintablePass;
